import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Divider,
  Chip,
  Fab,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { useCreditMemoSendRequests } from "src/hooks/credit-memo-send/useCreditMemoSendRequests";
import StatusChip from "../general/StatusChip";
import { VertexCreditMemoRequest } from "models/vertex-credit-memo";

const CreditMemoSends: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(
    generateCurrentMonthValue()
  );
  const { requests, loading, fetchByMonth } = useCreditMemoSendRequests();
  const navigate = useNavigate();
  const months = generateMonths();

  useEffect(() => {
    fetchByMonth(selectedMonth);
  }, [selectedMonth]);

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };

  const groupRecordsByDate = (
    records: VertexCreditMemoRequest[]
  ): { [date: string]: VertexCreditMemoRequest[] } => {
    return records.reduce((groups, record) => {
      const date = record.createdAt.substring(0, 10);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(record);
      return groups;
    }, {} as { [date: string]: VertexCreditMemoRequest[] });
  };

  const groupedRecords = groupRecordsByDate(requests);

  return (
    <Container sx={{ pt: 4, pb: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <IconButton
          onClick={() => navigate("/")}
          size="small"
          sx={{ alignSelf: "flex-start", flexGrow: 0 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1, textAlign: "center" }}>
          France Credit Memo Send
        </Typography>
        <Box sx={{ flexGrow: 0, width: "48px" }} />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <Typography variant="h6" mr={2}>
          Select Month:
        </Typography>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          displayEmpty
          inputProps={{ "aria-label": "Select Month" }}
        >
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" gutterBottom>
        France Credit Memo Send Records
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : Object.keys(groupedRecords).length === 0 ? (
        <Typography>No credit memo send records found</Typography>
      ) : (
        <Box>
          {Object.keys(groupedRecords).map((date) => (
            <Box key={date} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {new Date(date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              {groupedRecords[date].map((record: VertexCreditMemoRequest) => (
                <Box
                  key={record.requestId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    borderBottom: "1px solid #ccc",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() =>
                    navigate(`/credit-memos/CMSEND-${record.requestId}`)
                  }
                >
                  <Box sx={{ flex: "1 1 16%", minWidth: "130px" }}>
                    <Typography variant="body1">
                      CMSEND-{record.requestId}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 40%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minWidth: "280px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      Credit Memo: {record.input.creditMemoTranId}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 10%",
                      display: "flex",
                      justifyContent: "center",
                      minWidth: "90px",
                    }}
                  >
                    <Chip
                      label={record.output.decision}
                      color={
                        record.output.decision === "PASS" ? "success" : "error"
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 10%",
                      display: "flex",
                      justifyContent: "center",
                      minWidth: "100px",
                    }}
                  >
                    <StatusChip status={record.status} />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 18%",
                      display: "flex",
                      justifyContent: "flex-end",
                      minWidth: "150px",
                    }}
                  >
                    <Typography variant="body2">
                      {new Date(record.createdAt).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}

      <Fab
        color="primary"
        size="large"
        variant="extended"
        sx={{
          position: "fixed",
          bottom: 30,
          right: 24,
          zIndex: 1000,
        }}
        onClick={() => navigate("/credit-memos/form")}
      >
        <AddIcon sx={{ mr: 1 }} />
        Create Credit Memos
      </Fab>
    </Container>
  );
};

const generateCurrentMonthValue = (): string => {
  return new Date().toISOString().substring(0, 7);
};

const generateMonths = (): { value: string; label: string }[] => {
  const startMonth = new Date(2024, 7, 2, 12, 0, 0);
  const currentMonth = new Date();
  const months = [];
  for (
    let dt = new Date(startMonth);
    dt <= currentMonth;
    dt.setMonth(dt.getMonth() + 1)
  ) {
    const value = dt.toISOString().substring(0, 7);
    const label = dt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    months.push({ value, label });
  }
  return months.reverse();
};

export default CreditMemoSends;
